import { StrapiMedia } from './recipes.model';

/**
 * Strapi query params populating light + dark icon media.
 *
 * Valid on `/api/recipes` and `/api/recipe-language-frameworks` only.
 * `/api/recipe-categories` has no media field and rejects these with a 400
 * `ValidationError: Invalid key iconImage`.
 */
export const RECIPE_ICON_POPULATE_PARAMS = {
  'populate[iconImage]': 'true',
  'populate[iconImageDark]': 'true'
} as const;

/** Icon media shape for single-media sources (language / framework). */
export interface StrapiSingleIconSource {
  iconImage?: StrapiMedia;
  iconImageDark?: StrapiMedia;
}

/** Icon media shape for repeatable-media sources (recipe). */
export interface StrapiArrayIconSource {
  iconImage?: StrapiMedia[];
  iconImageDark?: StrapiMedia[];
}

/**
 * Resolve a recipe icon URL for the active color scheme. Dark mode falls back
 * to the light asset when the CMS has no dark variant, so surfaces rendering on
 * a dark background must keep a visual fallback for that case — see
 * `hasDarkRecipeIcon`.
 */
export function resolveRecipeIconUrl(
  recipe: StrapiArrayIconSource,
  isDarkMode: boolean
): string | undefined {
  if (isDarkMode && recipe.iconImageDark?.[0]?.url) {
    return recipe.iconImageDark[0].url;
  }

  return recipe.iconImage?.[0]?.url;
}

/** True when the CMS provides a dedicated dark asset for this recipe. */
export function hasDarkRecipeIcon(recipe: StrapiArrayIconSource): boolean {
  return !!recipe.iconImageDark?.[0]?.url;
}

/** Resolve a language/framework icon URL for the active color scheme. */
export function resolveLanguageFrameworkIconUrl(
  lf: StrapiSingleIconSource,
  isDarkMode: boolean
): string | undefined {
  if (isDarkMode && lf.iconImageDark?.url) {
    return lf.iconImageDark.url;
  }

  return lf.iconImage?.url;
}

/** Build a slug → icon URL lookup for O(1) template access on OnPush renders. */
export function buildSlugIconUrlMap<T extends { slug: string }>(
  items: T[],
  resolve: (item: T) => string | undefined
): Record<string, string | undefined> {
  return items.reduce((map, item) => {
    map[item.slug] = resolve(item);
    return map;
  }, {} as Record<string, string | undefined>);
}
